import type { RosterSettings } from '../../../../types/scholarships';

/**
 * Client-side mirror of the API's RosterSettingsValidator, so Configure roster can say what is
 * wrong before a round trip. The API is still the authority; this only has to agree with it on
 * the rules a coach can actually break from the form.
 */
export const ROSTER_SETTINGS_LIMITS = {
  nameLength: 40,
  traits: 30,
  indicators: 20,
  optionList: 40,
} as const;

/**
 * The plain string lists on the settings, derived from the type so a new list cannot be missed:
 * OPTION_LIST_LABELS below must then name it, or the Record fails to compile.
 */
export type OptionListKey = {
  [K in keyof RosterSettings]: RosterSettings[K] extends string[] ? K : never;
}[keyof RosterSettings];

export const OPTION_LIST_LABELS: Record<OptionListKey, string> = {
  armAngles: 'Arm angles',
  pitchShapes: 'Pitch shapes',
  pitchingRoles: 'Pitching roles',
  playerTypes: 'Player types',
  playerStatuses: 'Player statuses',
};

const firstDuplicate = (names: readonly string[]): string | null => {
  const seen = new Set<string>();
  for (const name of names) {
    const folded = name.trim().toLowerCase();
    if (seen.has(folded)) return name.trim();
    seen.add(folded);
  }
  return null;
};

const checkNames = (label: string, names: readonly string[], max: number): string | null => {
  if (names.length > max) return `${label}: at most ${max}.`;
  if (names.some((n) => n.trim().length === 0)) return `${label}: every entry needs a name.`;
  const tooLong = names.find((n) => n.trim().length > ROSTER_SETTINGS_LIMITS.nameLength);
  if (tooLong) {
    return `${label}: "${tooLong.trim()}" is longer than ${ROSTER_SETTINGS_LIMITS.nameLength} characters.`;
  }
  const duplicate = firstDuplicate(names);
  if (duplicate) return `${label}: "${duplicate}" is listed twice.`;
  return null;
};

/** The first problem with a draft, worded for a coach, or null when it will save. */
export const validateRosterSettings = (settings: RosterSettings): string | null =>
  checkNames(
    'Evaluation tags',
    settings.evaluationTraits.map((t) => t.name),
    ROSTER_SETTINGS_LIMITS.traits,
  ) ??
  checkNames(
    'Custom indicators',
    settings.indicators.map((i) => i.name),
    ROSTER_SETTINGS_LIMITS.indicators,
  ) ??
  (Object.keys(OPTION_LIST_LABELS) as OptionListKey[]).reduce<string | null>(
    (problem, key) =>
      problem ?? checkNames(OPTION_LIST_LABELS[key], settings[key], ROSTER_SETTINGS_LIMITS.optionList),
    null,
  );

/** Names trimmed, as the API stores them, so an unchanged-looking draft compares equal. */
export const trimRosterSettings = (settings: RosterSettings): RosterSettings => ({
  evaluationTraits: settings.evaluationTraits.map((t) => ({ ...t, name: t.name.trim() })),
  indicators: settings.indicators.map((i) => ({ ...i, name: i.name.trim() })),
  handednessColors: { ...settings.handednessColors },
  armAngles: settings.armAngles.map((s) => s.trim()),
  pitchShapes: settings.pitchShapes.map((s) => s.trim()),
  pitchingRoles: settings.pitchingRoles.map((s) => s.trim()),
  playerTypes: settings.playerTypes.map((s) => s.trim()),
  playerStatuses: settings.playerStatuses.map((s) => s.trim()),
});

/**
 * Whether the draft differs from what the board has. Saving an untouched draft would mark a board
 * that runs on the defaults as customised, freezing today's defaults onto it.
 */
export const rosterSettingsChanged = (draft: RosterSettings, saved: RosterSettings): boolean =>
  JSON.stringify(trimRosterSettings(draft)) !== JSON.stringify(trimRosterSettings(saved));

/**
 * A new id for a trait or indicator. Player rows reference these, so they must never be reused or
 * derived from the name (a rename would orphan every player on it). The API accepts
 * `^[A-Za-z0-9_-]{1,40}$`; a UUID is 36 characters of exactly that.
 */
export const mintOptionId = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `id-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
};
